// Audio devices for calls: which mic / speaker to use, voice processing toggles, and a mic level meter.
// Saved per device in localStorage (private browsing still works, it just forgets).

export interface DevicePrefs {
  /** Microphone for calls ("" = system default). */
  micId: string;
  /** Speaker / headset that call audio plays on ("" = system default). */
  speakerId: string;
  /** Speaker the ringtone and alert sounds play on ("" = system default). */
  ringerId: string;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  /** Telnyx calling region picked by hand ("" = Telnyx default, or us-west / us-central / us-east / ca-central / eu). */
  region: string;
  /** Use the calling region the speed test measured fastest on this device (falls back to `region` until tested). */
  regionAuto: boolean;
  /**
   * Audio format for calls: "auto" / "opus" = HD voice (Opus, wideband: 1.10.27, on every device; before, computers
   * used G.711), "pcmu" = G.711, phone-line quality (narrowband).
   */
  codec: string;
}

const KEY = "joy.devices.v1";
const DEFAULTS: DevicePrefs = {
  micId: "",
  speakerId: "",
  ringerId: "",
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  region: "us-central", // nearest to the team (not San Jose); each device can still pick another in Settings
  regionAuto: true,
  codec: "auto",
};

/**
 * Codec order for calls. 1.10.27 "the phone allows HD calls: capture and send higher quality voice": HD (Opus, then
 * G.722, both wideband) first on every device; G.711 (PCMU, narrowband phone-line audio) only when picked. HD reaches
 * a cell phone when the office number has Telnyx's HD Voice on and the other phone's network carries HD (AT&T,
 * T-Mobile, Verizon on 4G/5G); otherwise Telnyx steps down to G.711 for that call. Opus also holds up better on weak
 * Wi-Fi / cellular.
 */
type CodecCapability = { mimeType: string; clockRate: number; channels?: number; sdpFmtpLine?: string };
export function preferredCodecs(choice = getDevicePrefs().codec): CodecCapability[] | undefined {
  if (typeof RTCRtpSender === "undefined" || !RTCRtpSender.getCapabilities) return undefined;
  const all = RTCRtpSender.getCapabilities("audio")?.codecs ?? [];
  const order = choice === "pcmu" ? [/PCMU/i] : [/opus/i, /G722/i];
  const first = order.flatMap((re) => all.filter((c) => re.test(c.mimeType)));
  if (!first.length) return undefined;
  return [...first, ...all.filter((c) => !first.includes(c))];
}

let cache: DevicePrefs | null = null;
const listeners = new Set<(p: DevicePrefs) => void>();

export function getDevicePrefs(): DevicePrefs {
  if (cache) return { ...cache };
  let saved: Partial<DevicePrefs> = {};
  try {
    saved = JSON.parse(localStorage.getItem(KEY) ?? "{}") ?? {};
  } catch {
    // private mode / blocked storage: defaults
  }
  cache = { ...DEFAULTS };
  for (const k of Object.keys(DEFAULTS) as (keyof DevicePrefs)[]) {
    if (typeof saved[k] === typeof DEFAULTS[k]) (cache as unknown as Record<string, unknown>)[k] = saved[k];
  }
  // 61.10.8: devices still on "Telnyx default" (which tends to route via San Jose) move once to US Central.
  // A region someone picked themselves (Montreal etc.) is kept, and they can pick again any time.
  try {
    if (!localStorage.getItem(`${KEY}.region61`)) {
      localStorage.setItem(`${KEY}.region61`, "1");
      if (!cache.region) {
        cache.region = "us-central";
        localStorage.setItem(KEY, JSON.stringify(cache));
      }
    }
  } catch {
    // storage blocked: this session uses US Central anyway
    if (!cache.region) cache.region = "us-central";
  }
  return { ...cache };
}

export function setDevicePrefs(partial: Partial<DevicePrefs>): DevicePrefs {
  const next = { ...getDevicePrefs() };
  for (const k of Object.keys(DEFAULTS) as (keyof DevicePrefs)[]) {
    const v = partial[k];
    if (v !== undefined && typeof v === typeof DEFAULTS[k]) (next as unknown as Record<string, unknown>)[k] = v;
  }
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // keep it for this session only
  }
  for (const fn of listeners) {
    try {
      fn({ ...next });
    } catch {
      // a listener's problem isn't ours
    }
  }
  return { ...next };
}

/** Called whenever device prefs change (voice.ts uses it to apply them to the phone line). */
export function onDevicePrefsChange(fn: (p: DevicePrefs) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** True when this browser can send audio to a chosen speaker (Chrome/Edge/Firefox desktop; not Safari/iOS). */
export function canPickSpeaker(): boolean {
  return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
}

/** getUserMedia audio constraints from the saved prefs (optionally for a specific mic). */
export function audioConstraints(micId = getDevicePrefs().micId): MediaTrackConstraints {
  const p = getDevicePrefs();
  const c: MediaTrackConstraints = {
    echoCancellation: p.echoCancellation,
    noiseSuppression: p.noiseSuppression,
    autoGainControl: p.autoGainControl,
    // 1.10.27 HD voice: the mic at full quality (48 kHz, one channel), which is what Opus sends. "ideal": a mic that
    // can't is used as it is, never refused.
    sampleRate: { ideal: 48000 },
    channelCount: { ideal: 1 },
  };
  if (micId) c.deviceId = { ideal: micId };
  return c;
}

/**
 * The mics and speakers on this device. Browsers hide device names until mic access is allowed:
 * if they're blank and `askPermission` is true, this asks for the mic once (call it from a tap).
 */
export async function listDevices(askPermission = true): Promise<{ mics: MediaDeviceInfo[]; speakers: MediaDeviceInfo[]; canPickSpeaker: boolean }> {
  const md = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
  if (!md?.enumerateDevices) return { mics: [], speakers: [], canPickSpeaker: false };
  let all = await md.enumerateDevices().catch(() => [] as MediaDeviceInfo[]);
  const needsLabels = all.some((d) => (d.kind === "audioinput" || d.kind === "audiooutput") && !d.label) || !all.some((d) => d.kind === "audioinput");
  if (needsLabels && askPermission && md.getUserMedia) {
    try {
      const s = await md.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      all = await md.enumerateDevices().catch(() => all);
    } catch {
      // permission denied: return what we have (unnamed devices)
    }
  }
  // Chrome lists each device again as "communications"; the "default" entry is useful, that one isn't.
  const keep = (d: MediaDeviceInfo) => d.deviceId !== "communications" && d.deviceId !== "";
  return {
    mics: all.filter((d) => d.kind === "audioinput" && keep(d)),
    speakers: all.filter((d) => d.kind === "audiooutput" && keep(d)),
    canPickSpeaker: canPickSpeaker(),
  };
}

/** Sends an element's audio to the chosen call speaker (or `deviceId`). Resolves false where unsupported. */
export async function applySpeaker(el: HTMLMediaElement, deviceId = getDevicePrefs().speakerId): Promise<boolean> {
  const sink = (el as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void>; sinkId?: string });
  if (typeof sink.setSinkId !== "function") return false;
  if ((sink.sinkId ?? "") === deviceId) return true;
  try {
    await sink.setSinkId(deviceId);
    return true;
  } catch {
    if (deviceId) {
      // the device is gone (unplugged headset): fall back to the default speaker
      await sink.setSinkId("").catch(() => {});
    }
    return false;
  }
}

/**
 * Live mic level (0..1) for a meter in settings, using the chosen mic and voice processing settings.
 * Returns a function that stops the mic. Call from a tap (it may ask for mic access).
 */
export function startMicLevel(onLevel: (level: number) => void, micId?: string): () => void {
  let stopped = false;
  let stream: MediaStream | null = null;
  let ctx: AudioContext | null = null;
  let raf = 0;
  const stop = () => {
    stopped = true;
    cancelAnimationFrame(raf);
    stream?.getTracks().forEach((t) => t.stop());
    void ctx?.close().catch(() => {});
    stream = null;
    ctx = null;
  };
  (async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints(micId) });
      if (stopped) return s.getTracks().forEach((t) => t.stop());
      stream = s;
      ctx = new AudioContext();
      if (ctx.state === "suspended") await ctx.resume().catch(() => {});
      const src = ctx.createMediaStreamSource(s);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.3;
      src.connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      let shown = 0;
      const tick = () => {
        if (stopped) return;
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        // dB-ish scale: -60 dBFS..0 → 0..1, fast attack and slower fall so the meter reads well
        const db = 20 * Math.log10(rms || 1e-8);
        const level = Math.min(1, Math.max(0, (db + 60) / 60));
        shown = level > shown ? level : shown * 0.85 + level * 0.15;
        onLevel(shown);
        raf = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      if (!stopped) onLevel(0);
      stop();
    }
  })();
  return stop;
}

// ---- "Mic on" for iPhone / iPad ------------------------------------------------------------------
// iOS forgets microphone permission once the mic has been off for about a minute (WebKit rule), so every
// call would ask again. While a mic stream stays open, iOS doesn't ask again (for up to 24 hours), so the
// dial pad's Mic switch opens one quiet stream and keeps it open while Joy Phone is on screen.

/** iPhone / iPad (including iPadOS that reports itself as a Mac). */
export const isIOS = () =>
  typeof navigator !== "undefined" && (/iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

let warmStream: MediaStream | null = null;
let micGranted = false;
/** iOS closed the mic by itself (screen locked, app sent to the background). */
const micEndedListeners = new Set<() => void>();
export function onMicClosedByPhone(fn: () => void): () => void {
  micEndedListeners.add(fn);
  return () => micEndedListeners.delete(fn);
}
const micListeners = new Set<() => void>();
const notifyMic = () => micListeners.forEach((fn) => fn());
export function onMicReadyChange(fn: () => void): () => void {
  micListeners.add(fn);
  return () => micListeners.delete(fn);
}

/**
 * Can a call start without a permission prompt? On iOS only while the mic stream is open; elsewhere once the
 * browser has remembered the permission.
 */
export function micReady(): boolean {
  if (isIOS()) return !!warmStream && warmStream.getAudioTracks().some((t) => t.readyState === "live");
  return micGranted || !!warmStream;
}
export const micIsOn = () => !!warmStream;
/** The open "mic on" stream (iPhone), if any — for the silence timer in micguard.ts. */
export const micStream = () => warmStream;

/** Checks the remembered permission (desktop browsers keep it), without prompting. */
/** The browser's own answer for the mic ("granted" = opening it won't ask), or "" when it won't say. */
let permState = "";
/** Opening the mic now would NOT put up an "Allow microphone?" prompt. */
export const micOpensSilently = () => permState === "granted";

export async function checkMicPermission(): Promise<void> {
  try {
    const st = await navigator.permissions?.query({ name: "microphone" as PermissionName });
    if (!st) return;
    micGranted = st.state === "granted";
    permState = st.state;
    st.onchange = () => {
      micGranted = st.state === "granted";
      permState = st.state;
      notifyMic();
    };
    notifyMic();
  } catch {
    // older browsers: the Mic switch asks
  }
}

/**
 * iPhone: keeps one audio mode ("play and record") for the whole time the app is open. Otherwise iOS switches
 * between its playback and calling modes every time the mic starts or stops (opening the app, the Mic switch,
 * an update reload), and that switch is the click / sound people hear. Safari 16.4+ (navigator.audioSession).
 */
export function steadyAudioSession() {
  if (!isIOS()) return;
  try {
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (session && session.type !== "play-and-record") session.type = "play-and-record";
  } catch {
    // older iOS: nothing to set
  }
}

/** Turns the mic on (from a tap): asks once, then keeps it open on iOS so calls don't ask again. */
export async function micOn(): Promise<boolean> {
  if (micReady() && (!isIOS() || warmStream)) return true;
  // iPhone that already allows the mic for Joy Phone (it won't ask): nothing needs to stay open. Calls open the mic
  // themselves without a prompt, and there's no mic to close and reopen (with iPhone's click) every time you switch
  // apps and come back.
  if (isIOS() && micOpensSilently()) {
    warmStream?.getTracks().forEach((t) => t.stop());
    warmStream = null;
    notifyMic();
    return true;
  }
  steadyAudioSession();
  try {
    const s = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints() });
    micGranted = true;
    if (!isIOS()) {
      // Desktop browsers remember the permission: no need to keep the mic (and the tab's mic light) on.
      s.getTracks().forEach((t) => t.stop());
      notifyMic();
      return true;
    }
    // Silence it: nothing is sent anywhere; the stream just keeps iOS from asking again.
    s.getAudioTracks().forEach((t) => {
      t.enabled = false;
      t.addEventListener("ended", () => {
        if (warmStream === s) {
          warmStream = null;
          notifyMic();
          micEndedListeners.forEach((fn) => fn());
        }
      });
    });
    warmStream?.getTracks().forEach((t) => t.stop());
    warmStream = s;
    notifyMic();
    return true;
  } catch {
    notifyMic();
    return false;
  }
}

/**
 * "Keep the mic ready" (the dial pad's Mic switch). Phones: off until you turn it on (the mic only opens for a
 * call, and closes after). Computers: on (they only remember the permission; no mic stays open).
 */
const MIC_KEEP_KEY = "joy.mic.keepReady";
const onPhone = () => isIOS() || (typeof navigator !== "undefined" && /Android.+Mobile/.test(navigator.userAgent));
export function micKeepReady(): boolean {
  try {
    const v = localStorage.getItem(MIC_KEEP_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    // default below
  }
  return !onPhone();
}
export function setMicKeepReady(on: boolean) {
  try {
    localStorage.setItem(MIC_KEEP_KEY, on ? "1" : "0");
  } catch {
    // this session only
  }
  notifyMic();
}

/** Before a call (placing or answering): open the mic first only if you keep it ready; otherwise the call opens it. */
export async function micForCall(): Promise<boolean> {
  if (!micKeepReady()) return true;
  // iPhone: never a prompt you didn't ask for. If the phone closed the mic and reopening it would ask, the call
  // starts without it instead and you turn it on in the call (or with the Mic switch).
  if (isIOS() && !micIsOn()) return true; // allowed: the call opens it without asking; not allowed: no surprise prompt
  return micOn();
}

export function micOff() {
  warmStream?.getTracks().forEach((t) => t.stop());
  warmStream = null;
  notifyMic();
}
